import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import api from "../lib/api";
import { formatDate, formatKm, formatPriceINR, resolveImageUrl } from "../lib/format";
import DeleteConfirmModal from "./DeleteConfirmModal";

const STATUSES = ["new","contacted","inspection_scheduled","offer_made","purchased","declined"];

const AdminSellLeads = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = async () => {
    const params = new URLSearchParams();
    if (filter) params.set("status", filter);
    const r = await api.get(`/admin/sell-car?${params.toString()}`);
    setItems(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const update = async (id, patch) => {
    try { const r = await api.patch(`/admin/sell-car/${id}`, patch); setItems(items.map((x) => x.id === id ? r.data : x)); toast.success("Updated"); }
    catch { toast.error("Failed"); }
  };

  const requestDelete = (it) => setPendingDelete({ id: it.id, label: `${it.name} — ${it.manufacturing_year} ${it.brand} ${it.model}${it.variant ? " " + it.variant : ""}` });
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/admin/sell-car/${pendingDelete.id}`);
      toast.success("Sell lead deleted");
      setPendingDelete(null);
      await load();
    } catch (e) {
      toast.error("Delete failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-sell-leads">
      <div>
        <div className="overline text-[#7A1519]">Sell Leads</div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tight font-semibold">Sell-Your-Car Submissions</h1>
      </div>
      <div className="flex gap-1 flex-wrap">
        <button onClick={() => setFilter("")} className={`text-xs px-3 py-1.5 rounded-sm border hairline ${filter === "" ? "bg-[#7A1519] text-white border-[#7A1519]" : "bg-white"}`}>All</button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`text-xs px-3 py-1.5 rounded-sm border hairline capitalize ${filter === s ? "bg-[#7A1519] text-white border-[#7A1519]" : "bg-white"}`}>{s.replace(/_/g, " ")}</button>
        ))}
      </div>

      <div className="space-y-3">
        {items.length === 0 && <div className="bg-white border hairline p-6 text-sm text-[#242424]/60 text-center rounded-sm">No submissions.</div>}
        {items.map((it) => (
          <div key={it.id} className="bg-white border hairline rounded-sm" data-testid={`sell-lead-${it.id}`}>
            <div className="p-5 flex items-start justify-between cursor-pointer" onClick={() => setExpanded(expanded === it.id ? null : it.id)}>
              <div>
                <div className="font-medium">{it.name} · <span className="text-[#242424]/70">{it.phone}</span></div>
                <div className="text-sm text-[#242424]/70 mt-1">
                  {it.manufacturing_year} {it.brand} {it.model} {it.variant} · {formatKm(it.kilometres)} · {it.fuel} · {it.transmission} · {it.ownership} owner
                </div>
                <div className="text-xs text-[#242424]/60 mt-1">
                  Condition: <span className="font-medium">{it.condition}</span>
                  {it.expected_price ? ` · Expected: ${formatPriceINR(it.expected_price)}` : ""}
                </div>
              </div>
              <div className="text-right">
                <select value={it.status} onChange={(e) => { e.stopPropagation(); update(it.id, { status: e.target.value }); }} onClick={(e) => e.stopPropagation()} className="bg-white border hairline px-2 py-1 rounded-sm text-xs capitalize" data-testid={`sell-status-${it.id}`}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
                <div className="text-[10px] text-[#242424]/50 mt-1">{formatDate(it.created_at)}</div>
                <button onClick={(e) => { e.stopPropagation(); requestDelete(it); }} className="mt-2 p-1.5 border hairline rounded-sm hover:bg-[#F7F6F3] text-[#7A1519]" title="Delete lead" data-testid={`sell-del-${it.id}`}><Trash2 size={13} /></button>
              </div>
            </div>
            {expanded === it.id && (
              <div className="border-t hairline p-5 space-y-4 bg-[#F7F6F3]/40">
                <div className="text-sm"><span className="overline text-[#242424]/60 mr-2">Email:</span>{it.email}</div>
                {it.location && <div className="text-sm"><span className="overline text-[#242424]/60 mr-2">Location:</span>{it.location}</div>}
                {it.registration_number && <div className="text-sm"><span className="overline text-[#242424]/60 mr-2">Reg. No:</span>{it.registration_number}</div>}
                {it.image_urls?.length > 0 && (
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                    {it.image_urls.map((u, i) => (
                      <a key={i} href={resolveImageUrl(u)} target="_blank" rel="noreferrer" className="aspect-square overflow-hidden rounded-sm border hairline">
                        <img src={resolveImageUrl(u)} alt="" className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                )}
                <div>
                  <label className="overline text-[#242424]/60 block mb-1">Internal notes</label>
                  <textarea rows={3} defaultValue={it.internal_notes} onBlur={(e) => e.target.value !== it.internal_notes && update(it.id, { internal_notes: e.target.value })} className="w-full bg-white border hairline rounded-sm p-3 text-sm" data-testid={`sell-notes-${it.id}`} />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <DeleteConfirmModal
        open={!!pendingDelete}
        recordType="sell lead"
        recordLabel={pendingDelete?.label || ""}
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default AdminSellLeads;
